import React, {useEffect, useState} from 'react';
import {Box, Button, CircularProgress, IconButton, Tooltip, Typography} from '@mui/material';
import {Key, KeyStatus, User} from "../types";
import {useAuth} from "../providers/AuthProvider";
import {useRetrieveUserInfo} from "../hooks/useRetrieveUserInfo";
import {ScreenWrapper} from "./ScreenWrapper";
import {ReactComponent as CheckIcon} from '../svgs/check-icon.svg';
import {ReactComponent as CopyIcon} from '../svgs/copy-icon.svg';
import {ReactComponent as ResetIcon} from '../svgs/reset-icon.svg';
import {ReactComponent as StatusIndicatorIcon} from '../svgs/status-indicator-icon.svg';
import {useHandleCopy} from "../hooks/useHandleCopy";
import {useAddKey} from "../hooks/useAddKey";
import {useResetKey} from "../hooks/useResetKey";
import {useExecute} from "../hooks/useExecute";
import ToggleButton from "./ToggleButton";
import {useScreenSize} from "../hooks/useScreenSize";

export const CustomerPortalScreen = () => {
    const [user, setUser] = useState<User | null | undefined>(undefined)
    const {retrieveUserInfo} = useRetrieveUserInfo()
    const {loggedInUser} = useAuth()
    const {handleCopy} = useHandleCopy()
    const {addKey} = useAddKey();
    const {resetKey} = useResetKey();
    const {execute, isLoading} = useExecute({user, setUser});
    const [isMacDownloadSelected, setIsMacDownloadSelected] = useState<boolean>(false)
    const {isScreenLessThan600} = useScreenSize()

    useEffect(() => {
        retrieveUserInfo(loggedInUser?.email ?? '').then((data) => setUser(data));
    }, []);

    const getStatusColor = (status: KeyStatus) => {
        if (status === 'BLOCKED') return "#FF4D4D";
        if (status === 'USED') return "#6CFF95";
        return "#A0A0A0";
    };

    return <ScreenWrapper hasLogoutButton={true}>
        {
            user === null &&
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                flex: '1 0 0'
            }}>
                <Typography sx={{
                    color: 'rgba(255, 255, 255, 0.50)',
                    fontFamily: 'Inter',
                    mx: '1.87rem',
                    textAlign: 'center',
                }}>
                    It seems you do not have a SPAWN subscription yet. To use this portal, you need to <a
                    href="https://www.tryspawn.com/#CTA" style={{color: '#FFF'}}>purchase SPAWN
                    first.</a> Please contact
                    customer support if you need help.
                </Typography>
            </Box>
        }
        {
            user === undefined &&
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '1.25rem',
                flex: '1 0 0'
            }}>
                <CircularProgress size="3rem" sx={{color: "#FFF"}}/>
                <Typography sx={{
                    color: 'rgba(255, 255, 255, 0.50)',
                    fontFamily: 'Inter',
                    mx: '1.87rem',
                    textAlign: 'center',
                }}>Fetching your data</Typography>
            </Box>
        }
        {
            user &&
            <Box sx={{
                display: 'flex',
                padding: '0 3.12rem',
                flexDirection: 'column',
                alignItems: "center",
                justifyContent: "center",
                gap: '1.25rem',
                flex: '1 0 0',
                alignSelf: 'stretch',
            }}>
                <Box sx={{
                    display: 'flex',
                    height: '3.12rem',
                    padding: '0 .62rem',
                    alignItems: 'center',
                    gap: '.62rem',
                    alignSelf: 'stretch',
                }}>
                    <Typography sx={{
                        color: '#FFF',
                        leadingTrim: 'both',
                        textEdge: 'cap',
                        fontFamily: 'Montserrat',
                        fontSize: '1.25rem',
                        fontStyle: 'normal',
                        fontWeight: '600',
                        lineHeight: 'normal',
                        flex: '1 0 0',
                    }}>
                        Order
                    </Typography>
                    <CheckIcon/>
                    <Typography sx={{
                        color: 'rgba(255, 255, 255, 0.50)',
                        leadingTrim: 'both',
                        textEdge: 'cap',
                        fontFamily: 'Inter',
                        fontSize: '.87rem',
                        fontStyle: 'normal',
                        fontWeight: '400',
                        lineHeight: 'normal',
                        textTransform: 'capitalize',
                    }}>
                        {user.status.replace('_', '').toLowerCase()}
                    </Typography>
                </Box>
                <Box sx={{
                    display: 'flex',
                    padding: '1rem 1.25rem',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    alignSelf: 'stretch',
                    borderRadius: '8px',
                    background: 'rgba(255, 255, 255, 0.05)',
                }}>
                    <Typography sx={{
                        color: 'rgba(255, 255, 255, 0.50)',
                        leadingTrim: 'both',
                        textEdge: 'cap',
                        fontFamily: 'Inter',
                        fontSize: '.87rem',
                        fontStyle: 'normal',
                        fontWeight: '400',
                        lineHeight: 'normal',
                    }}>
                        {isScreenLessThan600 && user.billingEmail.length > 23 ? user.billingEmail.toLowerCase().slice(0, 20) + '...' : user.billingEmail.toLowerCase()}
                    </Typography>
                    <IconButton sx={{padding: 0}} onClick={() => handleCopy(user.billingEmail.toLowerCase())}>
                        <CopyIcon/>
                    </IconButton>
                </Box>
                <Box sx={{
                    display: 'flex',
                    height: '3.12rem',
                    padding: '0 .62rem',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    alignSelf: 'stretch',
                }}>
                    <Typography sx={{
                        color: '#FFF',
                        leadingTrim: 'both',
                        textEdge: 'cap',
                        fontFamily: 'Montserrat',
                        fontSize: '1.25rem',
                        fontStyle: 'normal',
                        fontWeight: '600',
                        lineHeight: 'normal',
                    }}>
                        Licenses
                    </Typography>
                    <Typography sx={{
                        color: 'rgba(255, 255, 255, 0.50)',
                        leadingTrim: 'both',
                        textEdge: 'cap',
                        fontFamily: 'Inter',
                        fontSize: '.87rem',
                        fontStyle: 'normal',
                        fontWeight: '400',
                        lineHeight: 'normal',
                    }}>
                        {Math.min(user.keys.length, 2)}/2
                    </Typography>
                </Box>
                {
                    user.keys.slice(0, 2).map((key: Key) => {
                        const isDisabled = key.status === 'NOT_USED' || user.status === 'BLOCKED' || isLoading;

                        return <Box sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '1px',
                            alignSelf: 'stretch',
                        }}>
                            <Tooltip
                                placement="top"
                                title={isDisabled ? "You cannot reset key when it's not used or blocked." : "Reset key. This will log out the user that uses that key."}>
                                <IconButton
                                    sx={{
                                        display: 'flex',
                                        padding: '1rem .62rem',
                                        alignItems: 'center',
                                        gap: '.62rem',
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        borderRadius: '8px 0 0 8px',
                                        '&.Mui-disabled': {
                                            background: `repeating-linear-gradient(
                                                -45deg,
                                                rgba(19, 19, 19, 1) 0, 
                                                rgba(19, 19, 19, 1) 6.25%,
                                                rgba(28, 28, 28, 1) 6.25%, 
                                                rgba(28, 28, 28, 1) 12.5%
                                            )`,
                                            pointerEvents: 'all',
                                        },
                                        '&.Mui-disabled:hover': {
                                            cursor: 'not-allowed',
                                        },
                                    }}
                                    onClick={() => execute(resetKey, key.value)}
                                    disabled={isDisabled}>
                                    <ResetIcon/>
                                </IconButton>
                            </Tooltip>
                            <Box sx={{
                                display: 'flex',
                                padding: '1rem 1.25rem 1rem .62rem',
                                alignItems: 'center',
                                gap: '.62rem',
                                flex: '1 0 0',
                                borderRadius: '0 8px 8px 0',
                                background: 'rgba(255, 255, 255, 0.05)',
                            }}>
                                <StatusIndicatorIcon style={{
                                    color: getStatusColor(key.status),
                                }}/>
                                <Typography sx={{
                                    color: 'rgba(255, 255, 255, 0.50)',
                                    leadingTrim: 'both',
                                    textEdge: 'cap',
                                    fontFamily: 'Inter',
                                    fontSize: '.81rem',
                                    fontStyle: 'normal',
                                    fontWeight: '400',
                                    lineHeight: 'normal',
                                    flexGrow: 1,
                                }}>{isScreenLessThan600 ? key.value.slice(0, 13) + '...' : key.value}</Typography>
                                <IconButton sx={{padding: 0}} onClick={() => handleCopy(key.value)}>
                                    <CopyIcon/>
                                </IconButton>
                            </Box>
                        </Box>;
                    })
                }
                {
                    user.keys.length <= 1 &&
                    <Button
                        sx={{
                            display: 'flex',
                            padding: '1rem 1.25rem',
                            alignSelf: 'stretch',
                            borderRadius: '8px',
                            background: 'rgba(255, 255, 255, 0.05)',
                            color: 'rgba(255, 255, 255, 0.70)',
                            leadingTrim: 'both',
                            textEdge: 'cap',
                            fontFamily: 'Inter',
                            fontSize: '.87rem',
                            fontStyle: 'normal',
                            fontWeight: '400',
                            lineHeight: 'normal',
                            textAlign: 'center',
                            textTransform: 'none',
                        }}
                        onClick={() => execute(addKey)}
                        disabled={isLoading}>
                        {isLoading ?
                            <CircularProgress size="1rem" sx={{color: "#FFFFFF"}}/> : "Generate a second license."}
                    </Button>
                }

                <Typography sx={{
                    color: 'rgba(255, 255, 255, 0.50)',
                    leadingTrim: 'both',
                    textEdge: 'cap',
                    fontFamily: 'Inter',
                    fontSize: '.87rem',
                    fontStyle: 'normal',
                    fontWeight: '400',
                    lineHeight: 'normal',
                    textAlign: 'center',
                    mx: 'auto',
                    pb: '.5rem'
                }}>
                    {
                        user.usedCredits > 0 ?
                            <>
                            <span style={{
                                color: '#FF7044',
                                fontFamily: 'Inter',
                                fontSize: '.87rem',
                                fontStyle: 'normal',
                                fontWeight: '400',
                                lineHeight: 'normal',
                            }}>{user.usedCredits}</span>/{user.totalCredits} credits used this month.</> :
                            <>No credits used. You haven't used SPAWN this month.</>
                    }
                </Typography>

                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '1.25rem',
                    alignSelf: 'stretch',
                    mt: 1
                }}>
                    <ToggleButton checked={isMacDownloadSelected} setChecked={setIsMacDownloadSelected}/>
                </Box>

                <Button component="a"
                        href={
                            isMacDownloadSelected
                                ? "https://storage.googleapis.com/spawn-builds/2.0.2/Mac%20Spawn-Full%202.0.2.zip"
                                : "https://storage.googleapis.com/spawn-builds/2.0.2/Win%20Spawn-Full%202.0.2.zip"
                        }
                        download
                        sx={{
                            display: 'flex',
                            padding: '1rem 1.25rem',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '.62rem',
                            alignSelf: 'stretch',
                            borderRadius: '8px',
                            background: '#FF7044',
                            color: '#000',
                            leadingTrim: 'both',
                            textEdge: 'cap',
                            fontFamily: 'Montserrat',
                            fontSize: '.87rem',
                            fontStyle: 'normal',
                            fontWeight: '600',
                            lineHeight: 'normal',
                            textTransform: 'none',
                        }}
                >
                    Download SPAWN (v2.0.2)
                </Button>

                <Box>
                    <Button component="a"
                            href={
                                isMacDownloadSelected
                                    ? "https://storage.googleapis.com/spawn-builds/2.0.2/Mac%20Spawn-Updater%202.0.2.zip"
                                    : "https://storage.googleapis.com/spawn-builds/2.0.2/Win%20Spawn-Updater%202.0.2.zip"
                            }
                            download
                            sx={{
                                display: 'flex',
                                padding: '1rem 1.25rem',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '.62rem',
                                alignSelf: 'stretch',
                                borderRadius: '8px',
                                border: '1px solid #FF7044',
                                color: '#FF7044',
                                leadingTrim: 'both',
                                textEdge: 'cap',
                                fontFamily: 'Montserrat',
                                fontSize: '.87rem',
                                fontStyle: 'normal',
                                fontWeight: '600',
                                lineHeight: 'normal',
                                textTransform: 'none',
                            }}
                    >
                        Download SPAWN Updater (v2.0.2)
                    </Button>

                    <Typography sx={{textAlign: 'center', fontSize: '.6rem', color: '#FF7044', mt: 1, mb: 3}}>Warning: use this
                        updater only if you have SPAWN installed already and want to upgrade to the latest
                        version</Typography>
                </Box>
            </Box>
        }
    </ScreenWrapper>
}
